import React, { createContext, useState, useEffect } from 'react';
import { API_BASE, API_PROXY } from '../utils/env';

export const UiSettingsContext = createContext();

// Default nav items - mirrors backend DEFAULT_NAV_ITEMS
const defaultNavConfig = {
  items: [
    { key: 'home', label: 'Home', visible: true, order: 0 },
    { key: 'dashboard', label: 'Dashboard', visible: true, order: 1 },
    { key: 'campus_summary', label: 'Campus Summary', visible: true, order: 2 },
    { key: 'transfers', label: 'Transfers', visible: true, order: 3 },
    { key: 'position_budgeting', label: 'Position Budgeting', visible: true, order: 4 },
    { key: 'personnel_what_if', label: 'Personnel What-If', visible: true, order: 5 },
    { key: 'planning', label: 'Planning', visible: true, order: 6 },
    { key: 'research', label: 'Research', visible: true, order: 7 },
    { key: 'tuition_planning', label: 'Tuition Planning', visible: true, order: 8 },
    { key: 'budget_vs_actual', label: 'Budget vs Actual', visible: true, order: 9 },
    { key: 'budget_scenarios', label: 'Budget Scenarios', visible: true, order: 10 },
    { key: 'program_margins', label: 'Program Margins', visible: true, order: 11 },
    { key: 'revenue_recognition', label: 'Revenue Recognition', visible: true, order: 12 },
    { key: 'enrollment_funnel', label: 'Enrollment Funnel', visible: true, order: 13 },
    { key: 'yield_analysis', label: 'Yield Analysis', visible: true, order: 14 },
    { key: 'reports', label: 'Analytics BI', visible: true, order: 15 },
    { key: 'courses', label: 'Courses', visible: true, order: 16 },
  ],
  primaryKeys: ['home', 'dashboard', 'campus_summary'],
};

const defaultSettings = {
  show_personnel: true,
  show_account_overview: true,
  show_costcenter_overview: true,
  show_fund_overview: true,
  show_supplement_grouping: true,
  group_by_mode: 'ledger',
  branding_logo_url: null,
  branding_logo_scale: 1,
  branding_favicon_url: null,
  branding_app_name: 'Uni-Fund',
  branding_primary_color: null,
  branding_accent_color: null,
  branding_hero_gradient: null,
  branding_font_family: null,
  branding_login_background: null,
  branding_welcome_message: null,
  enable_infinite_scroll: true,
  infinite_scroll_threshold: 0.1,
  personnel_page_size: 50,
  positions_page_size: 50,
  transfers_page_size: 50,
  budget_book_revenue_labels: {},
  nav_config: defaultNavConfig,
};

const buildApiUrl = (path) => {
  if (path.startsWith('http')) return path;
  const cleaned = path.startsWith('/') ? path : `/${path}`;
  const withApi = cleaned.startsWith('/api') ? cleaned : `/api${cleaned}`;
  if (!API_BASE) return withApi; // dev proxy fallback
  return `${API_BASE}${withApi}`;
};

const resolveApiBase = () => {
  if (API_PROXY) return API_PROXY;
  if (API_BASE) {
    // If an /api suffix is present, strip it for static assets
    return API_BASE.replace(/\/api$/, '');
  }
  try {
    const url = new URL(window.location.href);
    const guess = `${url.protocol}//${url.hostname}:4000`;
    return guess.replace(/\/$/, '');
  } catch (e) {
    return '';
  }
};

const normalizeBranding = (settings) => {
  const base = resolveApiBase();
  const normalizeUrl = (url) => {
    if (!url) return null;
    // Drop the old default so we fall back to bundled asset
    if (url === '/static/assets/images/uni-fund-logo.png' || url === '/static/assets/images/unifund-favicon.ico') {
      return null;
    }
    if (url.startsWith('/static') && !base) {
      return null;
    }
    return url;
  };
  return {
    ...settings,
    branding_logo_url: normalizeUrl(settings.branding_logo_url),
    branding_favicon_url: normalizeUrl(settings.branding_favicon_url),
  };
};

export const UiSettingsProvider = ({ children }) => {
  const [settings, setSettings] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem('uiSettings')) || {};
      const merged = { ...defaultSettings, ...saved };
      return normalizeBranding(merged);
  } catch (e) {
      return { ...defaultSettings };
  }
  });

  // Function to fetch and apply settings from server
  const refreshSettings = () => {
    return fetch(buildApiUrl('/ui-settings'))
      .then(res => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then(data => {
        const merged = normalizeBranding({ ...defaultSettings, ...data });
        setSettings(merged);
        localStorage.setItem('uiSettings', JSON.stringify(merged));

        // Apply document-level branding on load
        if (merged.branding_app_name) {
          document.title = merged.branding_app_name;
        }
        if (merged.branding_primary_color) {
          document.documentElement.style.setProperty('--brand', merged.branding_primary_color);
        }
        if (merged.branding_accent_color) {
          document.documentElement.style.setProperty('--accent', merged.branding_accent_color);
        }
        if (merged.branding_hero_gradient) {
          document.documentElement.style.setProperty('--hero-gradient', merged.branding_hero_gradient);
        } else {
          document.documentElement.style.removeProperty('--hero-gradient');
        }
        if (merged.branding_favicon_url) {
          let link = document.querySelector("link[rel*='icon']");
          if (!link) {
            link = document.createElement('link');
            link.rel = 'icon';
            document.head.appendChild(link);
          }
          link.href = merged.branding_favicon_url;
        }
        return merged;
      })
      .catch(err => console.error('Failed to fetch UI settings from server:', err));
  };

  useEffect(() => {
    refreshSettings();
  }, []);

  // Listen for localStorage changes from other tabs (e.g., admin panel)
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'uiSettings' && e.newValue) {
        try {
          const newSettings = JSON.parse(e.newValue);
          const merged = normalizeBranding({ ...defaultSettings, ...newSettings });
          setSettings(merged);
        } catch (err) {
          console.error('Failed to parse updated UI settings:', err);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  useEffect(() => {
    const resolveAssetUrl = (url) => {
      if (!url) return null;
      if (url.startsWith('/static')) {
        const base = resolveApiBase();
        if (!base) return null;
        return `${base}${url}`;
      }
      return url;
    };

    if (settings.branding_app_name) {
      document.title = settings.branding_app_name;
    }
    if (settings.branding_primary_color) {
      document.documentElement.style.setProperty('--brand', settings.branding_primary_color);
    }
    if (settings.branding_accent_color) {
      document.documentElement.style.setProperty('--accent', settings.branding_accent_color);
    }
    if (settings.branding_hero_gradient) {
      document.documentElement.style.setProperty('--hero-gradient', settings.branding_hero_gradient);
    } else {
      document.documentElement.style.removeProperty('--hero-gradient');
    }
    if (settings.branding_font_family) {
      // Load Google Font dynamically
      const fontName = settings.branding_font_family.replace(/ /g, '+');
      const linkId = 'branding-google-font';
      let fontLink = document.getElementById(linkId);
      if (!fontLink) {
        fontLink = document.createElement('link');
        fontLink.id = linkId;
        fontLink.rel = 'stylesheet';
        document.head.appendChild(fontLink);
      }
      fontLink.href = `https://fonts.googleapis.com/css2?family=${fontName}:wght@400;500;600;700&display=swap`;
      document.documentElement.style.setProperty('--font-family', settings.branding_font_family);
      document.body.style.fontFamily = `'${settings.branding_font_family}', system-ui, -apple-system, sans-serif`;
    } else {
      document.documentElement.style.removeProperty('--font-family');
      document.body.style.fontFamily = '';
      // Remove custom font link if exists
      const fontLink = document.getElementById('branding-google-font');
      if (fontLink) fontLink.remove();
    }
    if (settings.branding_login_background) {
      document.documentElement.style.setProperty('--login-background', settings.branding_login_background);
    } else {
      document.documentElement.style.removeProperty('--login-background');
    }
    const faviconUrl = resolveAssetUrl(settings.branding_favicon_url) || '/favicon.ico';
    if (faviconUrl) {
      let link = document.querySelector("link[rel*='icon']");
      if (!link) {
        link = document.createElement('link');
        link.rel = 'icon';
        document.head.appendChild(link);
      }
      link.href = faviconUrl;
    }
  }, [settings.branding_app_name, settings.branding_primary_color, settings.branding_accent_color, settings.branding_hero_gradient, settings.branding_font_family, settings.branding_login_background, settings.branding_favicon_url]);

  const toggleSetting = key => {
    setSettings(prev => {
      const updated = { ...prev, [key]: !prev[key] };
      localStorage.setItem('uiSettings', JSON.stringify(updated));
      fetch(buildApiUrl('/ui-settings'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ [key]: updated[key] })
      }).catch(err => console.error('Failed to update UI settings on server:', err));
      return updated;
    });
  };

  return (
    <UiSettingsContext.Provider value={{ settings, toggleSetting, refreshSettings }}>
      {children}
    </UiSettingsContext.Provider>
  );
};
